import { SYMBOL_HYPHEN } from '../../../constants/common'
import {
  CONTRACTOR_USER_ROLE,
  DWELLER_USER_ROLE,
  EXTERNAL_CONTACT_USER_ROLE,
  MANAGER_USER_ROLE,
} from '../../../constants/user'
import {
  getBuildingProfilePathname,
  getFlatProfilePathname,
} from '../../../utils/link'
import { getUserProfileRoute } from '../../../utils/user'

export const REQUEST_LOG_EVENTS_PAGE_SIZE = 20

export const REQUEST_LOG_EVENT_ACTION = {
  ADD_ASSIGNEES: 'add_assignees',
  ADD_CONTRACTORS: 'add_contractors',
  ADD_DOCUMENT_STAPLINGS: 'add_document_staplings',
  ADD_LABELS: 'add_labels',
  ADD_RELATED_REQUEST: 'add_related_request',
  ADD_WORKERS: 'add_workers',
  ARCHIVE: 'archive',
  CHANGE_VISIBLE_TO_DWELLERS: 'change_visible_to_dwellers',
  CREATE: 'create',
  DELETE_ASSIGNEES: 'delete_assignees',
  DELETE_CONTRACTORS: 'delete_contractors',
  DELETE_DOCUMENT_STAPLINGS: 'delete_document_staplings',
  DELETE_LABELS: 'delete_labels',
  DELETE_RELATED_REQUEST: 'delete_related_request',
  DELETE_WORKERS: 'delete_workers',
  OVERDUE: 'overdue',
  RESTORE: 'restore',
  UPDATE_BUILDING: 'update_building',
  UPDATE_CATEGORY: 'update_category',
  UPDATE_DUE_DATE: 'update_due_date',
  UPDATE_FLAT: 'update_flat',
  UPDATE_OWNER: 'update_owner',
  UPDATE_STATUS: 'update_status',
  UPDATE_TEXT: 'update_text',
  UPDATE_TITLE: 'update_title',
}

export const REQUEST_LOG_EVENT_ACTION_TRANSLATION_KEY = {
  [REQUEST_LOG_EVENT_ACTION.ADD_ASSIGNEES]: 'RequestLogEvent.AssigneesAddedKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_CONTRACTORS]:
    'RequestLogEvent.ProvidersAddedKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_DOCUMENT_STAPLINGS]:
    'RequestLogEvent.DocFlowsAttachedKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_LABELS]: 'RequestLogEvent.LabelsAddedKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_RELATED_REQUEST]:
    'RequestLogEvent.RelatedRequestsAttachedKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_WORKERS]: 'RequestLogEvent.SideworkersAddedKey',
  [REQUEST_LOG_EVENT_ACTION.ARCHIVE]: 'RequestLogEvent.ArchivedKey',
  [REQUEST_LOG_EVENT_ACTION.CHANGE_VISIBLE_TO_DWELLERS]:
    'RequestLogEvent.VisibilityChangedKey',
  [REQUEST_LOG_EVENT_ACTION.CREATE]: 'RequestLogEvent.CreatedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_ASSIGNEES]:
    'RequestLogEvent.AssigneesRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_CONTRACTORS]:
    'RequestLogEvent.ProvidersRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_DOCUMENT_STAPLINGS]:
    'RequestLogEvent.DocFlowsDetachedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_LABELS]: 'RequestLogEvent.LabelsRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_RELATED_REQUEST]:
    'RequestLogEvent.RelatedRequestsDetachedKey',
  [REQUEST_LOG_EVENT_ACTION.DELETE_WORKERS]:
    'RequestLogEvent.SideworkersRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.OVERDUE]: 'RequestLogEvent.OverdueKey',
  [REQUEST_LOG_EVENT_ACTION.RESTORE]: 'RequestLogEvent.RestoredKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_BUILDING]:
    'RequestLogEvent.BuildingChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_CATEGORY]:
    'RequestLogEvent.CategoryChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_DUE_DATE]:
    'RequestLogEvent.DueDateChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_FLAT]: 'RequestLogEvent.FlatChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_OWNER]:
    'RequestLogEvent.ApplicantChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_STATUS]: 'RequestLogEvent.StatusChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_TEXT]: 'RequestLogEvent.TextChangedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_TITLE]: 'RequestLogEvent.TitleChangedKey',
}

// For nullable fields the backend sends one action both ways; the key is picked
// by `data`: empty old_value -> set key, empty new_value -> removed key,
// otherwise the base "changed" key.
export const REQUEST_LOG_EVENT_ACTION_SET_TRANSLATION_KEY = {
  [REQUEST_LOG_EVENT_ACTION.UPDATE_BUILDING]: 'RequestLogEvent.BuildingSetKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_CATEGORY]: 'RequestLogEvent.CategorySetKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_DUE_DATE]: 'RequestLogEvent.DueDateSetKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_FLAT]: 'RequestLogEvent.FlatSetKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_OWNER]: 'RequestLogEvent.ApplicantSetKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_TEXT]: 'RequestLogEvent.TextSetKey',
}

// The add-user events may carry `data.comment` (the comment emailed to the
// assigned users); a non-empty comment switches to the "with a comment" key.
export const REQUEST_LOG_EVENT_ACTION_WITH_COMMENT_TRANSLATION_KEY = {
  [REQUEST_LOG_EVENT_ACTION.ADD_ASSIGNEES]:
    'RequestLogEvent.AssigneesAddedWithCommentKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_CONTRACTORS]:
    'RequestLogEvent.ProvidersAddedWithCommentKey',
  [REQUEST_LOG_EVENT_ACTION.ADD_WORKERS]:
    'RequestLogEvent.SideworkersAddedWithCommentKey',
}

export const REQUEST_LOG_EVENT_ACTION_REMOVED_TRANSLATION_KEY = {
  [REQUEST_LOG_EVENT_ACTION.UPDATE_BUILDING]:
    'RequestLogEvent.BuildingRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_CATEGORY]:
    'RequestLogEvent.CategoryRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_DUE_DATE]:
    'RequestLogEvent.DueDateRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_FLAT]: 'RequestLogEvent.FlatRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_OWNER]:
    'RequestLogEvent.ApplicantRemovedKey',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_TEXT]: 'RequestLogEvent.TextRemovedKey',
}

// Display-value key inside `data.old_value`/`data.new_value` for actions whose
// values are related objects (backend `values()` rows, hence `address__value`).
export const REQUEST_LOG_EVENT_RELATED_VALUE_KEY = {
  [REQUEST_LOG_EVENT_ACTION.UPDATE_BUILDING]: 'address__value',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_CATEGORY]: 'name',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_FLAT]: 'number',
  [REQUEST_LOG_EVENT_ACTION.UPDATE_OWNER]: 'fullname',
}

// `linkContext` carries `availableSections` plus the profile NG-availability
// flags selected by the tab component.
export const REQUEST_LOG_EVENT_ENTITY_LINK_CONFIG = {
  [REQUEST_LOG_EVENT_ACTION.UPDATE_BUILDING]: {
    getRoute: (id, { availableSections }) =>
      getBuildingProfilePathname({ availableSections, id }),
    idKey: 'id',
  },
  [REQUEST_LOG_EVENT_ACTION.UPDATE_FLAT]: {
    getRoute: (id, { availableSections }) =>
      getFlatProfilePathname({ availableSections, id }),
    idKey: 'id',
  },
  [REQUEST_LOG_EVENT_ACTION.UPDATE_OWNER]: {
    getRoute: (id, linkContext) =>
      getUserProfileRoute({ ...linkContext, id, role: DWELLER_USER_ROLE }),
    idKey: 'owner_id',
  },
}

// Role implied by the action for user-list events; picks the profile route the
// same way the card author link does (`getUserProfileRoute`).
export const REQUEST_LOG_EVENT_USER_ACTION_ROLE = {
  [REQUEST_LOG_EVENT_ACTION.ADD_ASSIGNEES]: MANAGER_USER_ROLE,
  [REQUEST_LOG_EVENT_ACTION.ADD_CONTRACTORS]: CONTRACTOR_USER_ROLE,
  [REQUEST_LOG_EVENT_ACTION.ADD_WORKERS]: EXTERNAL_CONTACT_USER_ROLE,
  [REQUEST_LOG_EVENT_ACTION.DELETE_ASSIGNEES]: MANAGER_USER_ROLE,
  [REQUEST_LOG_EVENT_ACTION.DELETE_CONTRACTORS]: CONTRACTOR_USER_ROLE,
  [REQUEST_LOG_EVENT_ACTION.DELETE_WORKERS]: EXTERNAL_CONTACT_USER_ROLE,
}

// Display-name key inside `data.items` entries for the M2M add/remove actions.
export const REQUEST_LOG_EVENT_ITEMS_NAME_KEY = {
  [REQUEST_LOG_EVENT_ACTION.ADD_ASSIGNEES]: 'fullname',
  [REQUEST_LOG_EVENT_ACTION.ADD_CONTRACTORS]: 'fullname',
  [REQUEST_LOG_EVENT_ACTION.ADD_LABELS]: 'name',
  [REQUEST_LOG_EVENT_ACTION.ADD_WORKERS]: 'fullname',
  [REQUEST_LOG_EVENT_ACTION.DELETE_ASSIGNEES]: 'fullname',
  [REQUEST_LOG_EVENT_ACTION.DELETE_CONTRACTORS]: 'fullname',
  [REQUEST_LOG_EVENT_ACTION.DELETE_LABELS]: 'name',
  [REQUEST_LOG_EVENT_ACTION.DELETE_WORKERS]: 'fullname',
}

export const REQUEST_LOG_EVENT_RELATED_REQUEST_ACTIONS = [
  REQUEST_LOG_EVENT_ACTION.ADD_RELATED_REQUEST,
  REQUEST_LOG_EVENT_ACTION.DELETE_RELATED_REQUEST,
]

export const REQUEST_LOG_EVENT_DOC_FLOW_ACTIONS = [
  REQUEST_LOG_EVENT_ACTION.ADD_DOCUMENT_STAPLINGS,
  REQUEST_LOG_EVENT_ACTION.DELETE_DOCUMENT_STAPLINGS,
]

export const REQUEST_LOG_EVENT_TRANSLATION_TAG_REGEXP =
  /<\/?(?:prevLink|currLink)>/g

export const REQUEST_LOG_EVENT_EMPTY_VALUE = SYMBOL_HYPHEN
